import {
  connectStoreMailRequestSchema,
  type ConnectStoreMailRequest,
  type StoreMail,
} from '@avrahami/contracts';
import { useState, type SyntheticEvent } from 'react';
import { useAsyncAction } from './auth';
import { Alert, Button, Card, ConfirmDialog, Loading, TextField } from './components';
import { formatDateTime, type ErrorOverrides } from './format';
import { NO_ERRORS, type createFieldChecks, type FieldErrors } from './forms';

/** The texts of the mail card; they come from the caller's dictionary. */
export interface StoreMailWords {
  title: string;
  intro: string;
  /** Shown to a reader who may not connect an account. */
  none: string;
  step1: string;
  step2: string;
  step3: string;
  address: string;
  appPassword: string;
  appPasswordHint: string;
  connect: string;
  connectedTo: string;
  /** Google refused the account's password at `when`. */
  failed: (address: string, when: string) => string;
  sendTest: string;
  testSent: string;
  refused: string;
  unreachable: string;
  notConnected: string;
  replace: string;
  replaceIntro: string;
  replaced: string;
  cancel: string;
  disconnect: string;
  disconnectTitle: string;
  disconnectBody: string;
}

/** The mail routes of one store, as the caller's surface names them. Each answers the account. */
export interface StoreMailActions {
  connect: (body: ConnectStoreMailRequest) => Promise<StoreMail>;
  disconnect: () => Promise<StoreMail>;
  sendTest: () => Promise<StoreMail>;
}

interface Shared {
  words: StoreMailWords;
  actions: StoreMailActions;
  /** The account changed: the caller keeps what it shows in step. */
  onChange: (mail: StoreMail) => void;
  errorMessage: (error: unknown, overrides?: ErrorOverrides) => string;
  checks: Pick<ReturnType<typeof createFieldChecks>, 'parseWith' | 'mergeErrors'>;
}

/**
 * A store's own Gmail account (adr/0027): every new quote request is mailed from it to itself.
 * Connecting is checked against Gmail at once, and the app password is never shown again. The
 * store's admin and the platform admin (adr/0028) show the same card, each over its own routes.
 */
export function StoreMailCard({
  mail,
  loadError,
  canWrite,
  timeZone,
  ...shared
}: Shared & {
  /** Undefined while it is being read. */
  mail: StoreMail | undefined;
  /** Why the account could not be read, if it could not. */
  loadError?: unknown;
  canWrite: boolean;
  /** The time zone the times are shown in; the browser's own when absent. */
  timeZone?: string;
}) {
  const { words, errorMessage } = shared;
  return (
    <Card title={words.title}>
      <div className="flex flex-col gap-4" data-testid="store-mail">
        <p className="text-sm text-slate-600">{words.intro}</p>
        {mail === undefined ? (
          loadError === undefined || loadError === null ? (
            <Loading />
          ) : (
            <Alert>{errorMessage(loadError)}</Alert>
          )
        ) : mail.gmailAddress === null ? (
          canWrite ? (
            <ConnectForm {...shared} />
          ) : (
            <Alert tone="info">{words.none}</Alert>
          )
        ) : (
          <Connected
            {...shared}
            mail={mail}
            address={mail.gmailAddress}
            canWrite={canWrite}
            timeZone={timeZone}
          />
        )}
      </div>
    </Card>
  );
}

const mailErrors = (words: StoreMailWords): ErrorOverrides => ({
  SERVICE_UNAVAILABLE: words.unreachable,
  CONFLICT: words.notConnected,
});

/** Connects an account, or puts another in the place of the connected one. */
function ConnectForm({
  words,
  actions,
  onChange,
  errorMessage,
  checks,
  replacing,
}: Shared & {
  /** Given when an account is already connected: the form can be put away. */
  replacing?: { onDone: () => void; onCancel: () => void };
}) {
  const [address, setAddress] = useState('');
  const [password, setPassword] = useState('');
  const [localErrors, setLocalErrors] = useState<FieldErrors>(NO_ERRORS);
  const connect = useAsyncAction(actions.connect);
  const errors = checks.mergeErrors(localErrors, connect.error);
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    const parsed = checks.parseWith(connectStoreMailRequestSchema, {
      gmailAddress: address,
      appPassword: password,
    });
    if (!parsed.ok) {
      setLocalErrors(parsed.errors);
      return;
    }
    setLocalErrors(NO_ERRORS);
    const result = await connect.run(parsed.value);
    if (!result.ok) return;
    replacing?.onDone();
    onChange(result.value);
  };
  return (
    <form
      onSubmit={(event) => {
        void submit(event);
      }}
      noValidate
      className="flex flex-col gap-4"
      data-testid="mail-connect"
    >
      {replacing === undefined ? null : (
        <p className="text-sm text-slate-700">{words.replaceIntro}</p>
      )}
      <ol className="list-decimal space-y-1 ps-5 text-sm text-slate-700">
        <li>{words.step1}</li>
        <li>
          {words.step2}{' '}
          <a
            href="https://myaccount.google.com/apppasswords"
            target="_blank"
            rel="noopener noreferrer"
            dir="ltr"
            className="font-medium text-brand-700 underline"
          >
            myaccount.google.com/apppasswords
          </a>
        </li>
        <li>{words.step3}</li>
      </ol>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label={words.address}
          name="gmailAddress"
          type="email"
          dir="ltr"
          autoComplete="off"
          maxLength={254}
          data-testid="mail-address-input"
          value={address}
          error={errors['gmailAddress']}
          onChange={(event) => {
            setAddress(event.target.value);
          }}
        />
        <TextField
          label={words.appPassword}
          name="appPassword"
          type="password"
          dir="ltr"
          autoComplete="new-password"
          maxLength={40}
          hint={words.appPasswordHint}
          data-testid="mail-password-input"
          value={password}
          error={errors['appPassword']}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />
      </div>
      {connect.error !== null && Object.keys(errors).length === 0 ? (
        <Alert>{errorMessage(connect.error, mailErrors(words))}</Alert>
      ) : null}
      <div className="flex flex-wrap gap-3">
        <Button type="submit" busy={connect.busy} data-testid="mail-connect-submit">
          {replacing === undefined ? words.connect : words.replace}
        </Button>
        {replacing === undefined ? null : (
          <Button tone="secondary" disabled={connect.busy} onClick={replacing.onCancel}>
            {words.cancel}
          </Button>
        )}
      </div>
    </form>
  );
}

/** What the card last did, said once under the account. */
type Notice = 'test-sent' | 'replaced' | null;

function Connected({
  mail,
  address,
  canWrite,
  timeZone,
  ...shared
}: Shared & {
  mail: StoreMail;
  address: string;
  canWrite: boolean;
  timeZone: string | undefined;
}) {
  const { words, actions, onChange, errorMessage } = shared;
  const [confirming, setConfirming] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  // Only the last action's refusal is shown.
  const [failed, setFailed] = useState<'test' | 'disconnect' | null>(null);
  const test = useAsyncAction(actions.sendTest);
  const disconnect = useAsyncAction(actions.disconnect);
  const failure =
    failed === 'test' ? test.error : failed === 'disconnect' ? disconnect.error : null;

  const sendTest = async () => {
    setNotice(null);
    setFailed(null);
    const result = await test.run();
    if (!result.ok) {
      setFailed('test');
      return;
    }
    setNotice('test-sent');
    onChange(result.value);
  };
  const remove = async () => {
    setNotice(null);
    setFailed(null);
    const result = await disconnect.run();
    setConfirming(false);
    if (result.ok) onChange(result.value);
    else setFailed('disconnect');
  };

  return (
    <>
      {mail.failedAt === null ? (
        <Alert tone="success">
          {words.connectedTo}{' '}
          <bdi dir="ltr" className="font-semibold" data-testid="mail-address">
            {address}
          </bdi>
        </Alert>
      ) : (
        <div data-testid="mail-failed">
          <Alert tone="warning">
            {words.failed(address, formatDateTime(mail.failedAt, timeZone))}
          </Alert>
        </div>
      )}
      {notice === 'replaced' ? <Alert tone="success">{words.replaced}</Alert> : null}
      {notice === 'test-sent' ? (
        <div data-testid="mail-test-sent">
          <Alert tone="success">{words.testSent}</Alert>
        </div>
      ) : null}
      {failure === null ? null : (
        <Alert>
          {errorMessage(failure, { ...mailErrors(words), VALIDATION_FAILED: words.refused })}
        </Alert>
      )}
      {!canWrite ? null : replacing ? (
        <ConnectForm
          {...shared}
          replacing={{
            onDone: () => {
              setReplacing(false);
              setNotice('replaced');
            },
            onCancel: () => {
              setReplacing(false);
            },
          }}
        />
      ) : (
        <div className="flex flex-wrap gap-3">
          <Button
            tone="secondary"
            busy={test.busy}
            data-testid="mail-test"
            onClick={() => {
              void sendTest();
            }}
          >
            {words.sendTest}
          </Button>
          <Button
            tone="secondary"
            data-testid="mail-replace"
            onClick={() => {
              setNotice(null);
              setFailed(null);
              setReplacing(true);
            }}
          >
            {words.replace}
          </Button>
          <Button
            tone="secondary"
            data-testid="mail-disconnect"
            onClick={() => {
              setConfirming(true);
            }}
          >
            {words.disconnect}
          </Button>
        </div>
      )}
      <ConfirmDialog
        open={confirming}
        title={words.disconnectTitle}
        confirmLabel={words.disconnect}
        tone="danger"
        confirmTestId="mail-disconnect-confirm"
        busy={disconnect.busy}
        onConfirm={() => {
          void remove();
        }}
        onClose={() => {
          setConfirming(false);
        }}
      >
        {words.disconnectBody}
      </ConfirmDialog>
    </>
  );
}
